import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import moment from 'moment-timezone';
import type { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { Calendar as BigCalendar, Views } from 'react-big-calendar';
import type {
  CalendarEventType,
  CalendarComponentEvent,
  CalendarPermissions,
} from '@axiom/validation';

import { CalendarPermissionsUtil } from '../../../utils/calendar-permissions-util';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { useInternalEventContext } from '../../../providers/InternalEventProvider';

import { CalendarDateUtil } from './calendar-date-util';
import { CalendarEventsUtil } from './calendar-events-util';
import { EVENT_STATES, WEEKEND_DAYS } from './calendar-events.const';
import { useCalendarMergeEvents } from './useCalendarMergeEvents';
import { useCalendarAccessors } from './useCalendarAccessors';
import { CalendarDeleteEventFlow } from './CalendarDeleteEventFlow';
import { useCalendarEventState } from './useCalendarEventState';
import { useCalendarEventHandlers } from './useCalendarEventHandlers';
import { useCalendarConfiguration } from './useCalendarConfiguration';
import { useCalendarEventStyling } from './useCalendarEventStyling';

// REQUIRED: DON'T REMOVE ======= START
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
// REQUIRED: DON'T REMOVE ======= END

const DnDCalendar = withDragAndDrop<CalendarComponentEvent>(BigCalendar);

/**
 * Default function to get the current date /time.
 * Defined outside component to avoid creating a new function on every render.
 */
const defaultGetCurrentDate = () => moment().toDate();

/**
 * Derives calendar permissions based on a device type.
 * Mobile devices always use base permissions regardless of passed permissions.
 *
 * @param permissions - The calendar permissions to use for non-mobile devices
 * @param isMobile - Whether the current device is mobile
 * @returns The appropriate calendar permissions for the device type
 */
export const getCalendarPermissions = ({
  permissions,
  isMobile,
}: {
  permissions: CalendarPermissions;
  isMobile: boolean;
}) => {
  return isMobile
    ? CalendarPermissionsUtil().getBasePermissions()
    : permissions;
};

export type UpdateEventArgType = Omit<
  EventInteractionArgs<CalendarComponentEvent>,
  'start' | 'end'
> & {
  start: CalendarComponentEvent['start'];
  end: CalendarComponentEvent['end'];
};

/**
 * Safely converts EventInteractionArgs to UpdateEventArgType.
 * Ensures start and end are proper Date objects.
 *
 * @throws {Error} if start or end are not valid Date objects
 */
export const toUpdateEventArg = (
  event: EventInteractionArgs<CalendarComponentEvent>
) => {
  // Validate that start and end are Date objects
  if (!(event.start instanceof Date)) {
    throw new Error('Event start must be a Date object');
  }

  if (!(event.end instanceof Date)) {
    throw new Error('Event end must be a Date object');
  }

  return {
    ...event,
    start: event.start,
    end: event.end,
  };
};

type TimeConfig = {
  increments?: number; // smallest selectable time
  slots?: number; // slots per section*
};

/* section = increments * slots
 * 1 * 60 = 1hr sections with 1hr selectable events
 * 2 * 30 = 1hr sections with 30min selectable events
 * */

export type CalendarProps = {
  backgroundEvents?: CalendarEventType[];
  dayMode?: TimeConfig | null;
  displayDate?: Date;
  editableEventId?: string | null;
  events: CalendarEventType[];
  /**
   * Function to get the current date/time. Used as a fallback when displayDate is not provided.
   * Defaults to moment().toDate() for backwards compatibility.
   * Inject a custom function in tests for deterministic behavior.
   */
  getCurrentDate?: () => Date;
  height?: string;
  name: string;
  onChange?: (
    data: CalendarEventType[],
    bgDate: CalendarEventType[]
  ) => void | Promise<void>;
  permissions: CalendarPermissions;
  scrollToTime?: Date;
  startMode?: typeof Views.WEEK | typeof Views.WORK_WEEK | typeof Views.DAY;
  timezone?: string;
  /** Rendered inside the toolbar between the Today button and the date navigation controls. */
  toolbarLeftSlot?: ReactNode;
  weekMode?: TimeConfig | null;
};

export const RawCalendar = ({
  backgroundEvents,
  dayMode,
  displayDate,
  editableEventId,
  events,
  getCurrentDate = defaultGetCurrentDate,
  height = '600px',
  name,
  onChange,
  permissions,
  scrollToTime,
  startMode = Views.WEEK,
  timezone,
  toolbarLeftSlot,
  weekMode,
}: CalendarProps) => {
  const safeBackgroundEvents = backgroundEvents || [];
  // Compute the fallback opening week once at mount, in whatever timezone is
  // active at that moment. Frozen for the life of the calendar so that a
  // mid-modal timezone change (e.g. AHQ's TalentTimezoneEditor or any
  // TIMEZONE_DROPDOWN) can't re-apply the Fri/Sat weekend-skip and yank the
  // user off their current week. `displayDate` (when provided by the parent
  // for edit-event-time anchoring) still wins on every render via the ??.
  const [openingFallbackDate] = useState<Date>(() => {
    const today = timezone
      ? moment.tz(getCurrentDate(), timezone)
      : moment(getCurrentDate());
    return WEEKEND_DAYS.has(today.weekday())
      ? today.startOf('week').add(1, 'week').toDate()
      : today.toDate();
  });
  const resolvedDisplayDate = displayDate ?? openingFallbackDate;

  const { isMobile } = useBreakpoint();
  const internalEventContext = useInternalEventContext();
  const perms = getCalendarPermissions({ permissions, isMobile });
  const timesConfig = useMemo(() => weekMode || dayMode, [weekMode, dayMode]);
  const mergeEvents = useCalendarMergeEvents();
  // Mutable ref for tracking background event IDs (used as a lookup cache)
  const bgEventIdsSetRef = useRef(new Set<CalendarEventType['id']>());
  const [selectedEvent, setSelectedEvent] =
    useState<CalendarComponentEvent | null>(null);
  // State for controlled calendar navigation - tracks user navigation
  const [userNavigatedDate, setUserNavigatedDate] = useState<Date | null>(null);

  // RBC-LOCALIZER-PATCH(dayshift): KEEP after moment→dayjs (dayjs has the same
  // one-directional browserTZOffset behaviour). Set the global moment timezone
  // *during render* — not in a layout effect — so react-big-calendar's first
  // render resolves "today" and per-day event placement in the configured
  // zone. A layout effect runs only AFTER the calendar's children have
  // rendered, by which point RBC has already resolved the current day against
  // the browser zone; when `timezone` is west of the browser that shifts
  // "today" (and events) back a day, which then hides behind a weekend column
  // in work-week view. See RBC issues #809 / #2122.
  // NOTE: Global timezone mutation makes tests non-deterministic and prevents
  // parallel test execution. This is a known trade-off the calendar lib forces.
  useMemo(() => {
    CalendarDateUtil.setMomentTimezone(timezone);
  }, [timezone]);

  useEffect(() => {
    return () => {
      // NOTE: Global timezone mutation here makes tests non-deterministic and prevents parallel test execution.
      // This is a known issue but not being addressed at this time.
      CalendarDateUtil.resetMomentTimezone(); // Reset to browser TZ on unmount
    };
  }, []);

  // Custom hooks for state management
  const {
    eventData,
    backgroundEventData,
    eventDataRef,
    backgroundEventDataRef,
    setEventData,
  } = useCalendarEventState({
    events,
    backgroundEvents: safeBackgroundEvents,
    perms,
    timezone,
    bgEventIdsSet: bgEventIdsSetRef.current,
    onChange,
    editableEventId,
  });

  // Custom hooks for event handlers
  const { handleSelectSlot, updateEvent, onDeleteHandler } =
    useCalendarEventHandlers({
      editableEventId,
      perms,
      bgEventIdsSet: bgEventIdsSetRef.current,
      mergeEvents,
      eventDataRef,
      backgroundEventDataRef,
      setEventData,
      selectedEvent,
      setSelectedEvent,
    });

  // Custom hooks for calendar configuration
  const { appLocalizer, defaultDate, toTime, formats, components } =
    useCalendarConfiguration({
      timezone,
      displayDate: resolvedDisplayDate,
      scrollToTime,
      toolbarLeftSlot,
    });

  // Custom hooks for event styling
  const { eventPropGetter, slotPropGetter } = useCalendarEventStyling({
    editableEventId,
    perms,
    timezone,
  });

  const { handleDraggableAccessor, handleResizableAccessor } =
    useCalendarAccessors(perms, editableEventId);

  const handleOnSelecting = (range: { start: Date; end: Date }) => {
    return (
      !CalendarEventsUtil.isInThePast(range.end, perms, timezone) &&
      (perms.eventBoundary.canDragToCreate || perms.staticEvent.canDragToCreate)
    );
  };

  const handleOnEventDrop = updateEvent;
  const handleOnEventResize = updateEvent;

  const onSelectHandler = (ev: CalendarComponentEvent) => {
    if (
      ev.isBackgroundEvent ||
      (ev.state === EVENT_STATES.SAVED &&
        ev.busy &&
        !perms.staticEvent.canDeleteSaved)
    ) {
      return;
    }

    setSelectedEvent(ev);
  };

  const handleNavigate = (newDate: Date) => {
    setUserNavigatedDate(newDate);
  };

  /**
   * This is for Internal Unit Test event signalling
   * START
   */
  useEffect(() => {
    const sub = internalEventContext.calendarCreationEvent.subscribe(value => {
      const [convertedValue] =
        CalendarEventsUtil.translateFromStartTimeEndTimeProps([value]) as [
          CalendarComponentEvent,
        ];

      handleSelectSlot({
        start: convertedValue.start,
        end: convertedValue.end,
        action: 'click',
      });
    });

    return () => {
      sub.unsubscribe();
    };
  }, [internalEventContext, handleSelectSlot]);
  /**
   * This is for Internal Unit Test event signalling
   * END
   */

  // Calendar will error if defaultView={startMode} is not one of the "views"
  return (
    <>
      <div
        data-test={name}
        className={editableEventId == null ? undefined : 'calendarEditMode'}
      >
        <DnDCalendar
          dayLayoutAlgorithm="no-overlap"
          onSelectEvent={onSelectHandler}
          date={userNavigatedDate || defaultDate}
          onNavigate={handleNavigate}
          defaultView={isMobile ? 'day' : startMode}
          drilldownView={null}
          events={eventData}
          localizer={appLocalizer}
          scrollToTime={toTime}
          views={[Views.WEEK, Views.WORK_WEEK, Views.DAY]}
          backgroundEvents={backgroundEventData}
          eventPropGetter={eventPropGetter}
          slotPropGetter={slotPropGetter}
          components={components}
          step={timesConfig?.increments}
          timeslots={timesConfig?.slots}
          style={{ height }}
          formats={formats}
          resizable={
            perms.staticEvent.canResize || perms.eventBoundary.canResize
          }
          selectable={
            editableEventId == null
              ? (perms.staticEvent.canCreate ||
                  perms.eventBoundary.canCreate ||
                  perms.staticEvent.canDragToCreate ||
                  perms.eventBoundary.canDragToCreate) &&
                'ignoreEvents'
              : 'ignoreEvents'
          }
          draggableAccessor={handleDraggableAccessor}
          resizableAccessor={handleResizableAccessor}
          onSelectSlot={handleSelectSlot}
          onSelecting={handleOnSelecting}
          onEventDrop={event => {
            handleOnEventDrop(toUpdateEventArg(event));
          }}
          onEventResize={event => {
            handleOnEventResize(toUpdateEventArg(event));
          }}
          showMultiDayTimes
        />
      </div>
      {selectedEvent ? (
        <CalendarDeleteEventFlow
          name="CALENDAR_DELETE_MODAL"
          permissions={perms}
          onDelete={onDeleteHandler}
          onClose={() => {
            setSelectedEvent(null);
          }}
          deleteEvent={selectedEvent}
          hideDelete={
            editableEventId != null || !!selectedEvent.isBackgroundEvent
          }
        />
      ) : null}
    </>
  );
};
